import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, TrendingUp, MessageSquare, Calendar,
    Hash, Copy, Shield, Terminal, Clock, Eye,
    Download, ArrowLeft, Users, Radio, Loader2, Lock
} from 'lucide-react';
import {
    useGetChannelsQuery,
    useGetStatsQuery,
    useLazyGetMessagesByChannelQuery,
    Channel,
} from '@/lib/services/telegramApi';
import { useGetOrganizationDetailsQuery } from '@/lib/services/organizationApi';
import { useFeatureAccess } from '@/contexts/FeatureAccessContext';
import { useToast } from '@/hooks/components/common/toast/useToast';

const ALLOWED_LICENSES = ['Enterprise', 'Enterprise Elite', 'MSSP'];

const isLicenseAuthorized = (licenseType?: string | null): boolean => {
    if (!licenseType) return false;
    const clean = licenseType.toLowerCase().replace(/[-_]/g, ' ').trim();
    return (
        clean === 'enterprise' ||
        clean === 'enterprise elite' ||
        clean === 'mssp' ||
        clean.startsWith('enterprise') ||
        clean.startsWith('mssp')
    );
};

const getEntityLabel = (entityType: string) => {
    switch (entityType) {
        case 'channel': return 'BROADCAST_NODE';
        case 'group': return 'COMMS_CLUSTER';
        default: return 'UNK_ENTITY';
    }
};

const HighlightText = ({ text, highlight }: { text: string, highlight: string }) => {
    if (!highlight.trim()) return <>{text}</>;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
        <>
            {parts.map((part, i) =>
                part.toLowerCase() === highlight.toLowerCase() ? (
                    <span key={i} className="bg-[#02DFC2]/20 text-[#02DFC2] font-bold px-1 rounded border border-[#02DFC2]/30">
                        {part}
                    </span>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </>
    );
};

const TelegramDashboard: React.FC = () => {
    const navigate = useNavigate();
    const showToast = useToast();

    const [selectedChat, setSelectedChat] = useState<Channel | null>(null);
    const [globalSearch, setGlobalSearch] = useState('');
    const [messageSearch, setMessageSearch] = useState('');
    const [copiedId, setCopiedId] = useState<number | null>(null);

    // --- LICENSE & ACCESS VERIFICATION ---
    const { data: orgData, isLoading: orgLoading } = useGetOrganizationDetailsQuery();
    const featureAccess = useFeatureAccess();

    const rawLicenseType =
        orgData?.data?.license?.type ||
        (orgData as any)?.license?.type ||
        (featureAccess as any)?.permissions?.licenseType ||
        (featureAccess as any)?.license;

    const isAuthorized = isLicenseAuthorized(rawLicenseType);

    // --- API QUERIES ---
    const { 
        data: channels, 
        isLoading: channelsLoading, 
        error: channelsError 
    } = useGetChannelsQuery(undefined, { skip: !isAuthorized });

    const { 
        data: stats, 
        isLoading: statsLoading,
        error: statsError
    } = useGetStatsQuery(undefined, { skip: !isAuthorized });

    const [fetchChatMessages, { data: chatMessages = [], isFetching: chatMessagesLoading }] = useLazyGetMessagesByChannelQuery();

    const isForbidden = 
        (channelsError as any)?.status === 403 || 
        (statsError as any)?.status === 403;

    const hasAccess = (isAuthorized && !isForbidden) || (Array.isArray(channels) && channels.length > 0);

    const filteredChats = useMemo(() => {
        if (!channels) return [];
        return channels
            .filter((chat) => chat.message_count > 0)
            .filter((chat) =>
                chat.title.toLowerCase().includes(globalSearch.toLowerCase()) ||
                chat.hash_id.toLowerCase().includes(globalSearch.toLowerCase())
            )
            .sort((a, b) => new Date(b.last_check_time).getTime() - new Date(a.last_check_time).getTime());
    }, [channels, globalSearch]);

    const visibleMessages = useMemo(() => {
        if (!chatMessages) return [];
        return [...chatMessages]
            .filter(msg => msg.text.toLowerCase().includes(messageSearch.toLowerCase()))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [chatMessages, messageSearch]);

    const handleChatSelect = (chat: Channel) => {
        setSelectedChat(chat);
        setMessageSearch('');
        fetchChatMessages({ channelHash: chat.hash_id, limit: 200 });
    };

    const handleCopy = (text: string, id: number) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        showToast({
            type: 'success',
            title: 'BUFFER_COPIED',
            message: 'Intel payload moved to local clipboard.'
        });
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div className="p-4 md:p-6 w-full flex flex-col h-[calc(100vh-1rem)] animate-fade-in space-y-6 max-w-[1800px] mx-auto font-mono selection:bg-[#02DFC2]/30 selection:text-white">

            {/* --- HEADER --- */}
            <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-2.5 bg-[#02DFC2]/10 border border-[#02DFC2]/20 rounded-xl">
                        <Terminal className="w-6 h-6 text-[#02DFC2]" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight uppercase">
                            DARK WEB PRIVATE FORUMS
                        </h2>
                    </div>
                </div>
            </div>

            {/* --- CLEARANCE CHECK / LOADING STATE --- */}
            {orgLoading ? (
                <div className="flex-1 flex flex-col items-center justify-center bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl p-12 text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-[#02DFC2] mb-4" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-[0.2em] mb-2">Verifying Node Security Clearance</h3>
                    <p className="text-[11px] text-slate-500 uppercase tracking-widest font-mono">Querying organizational license parameters...</p>
                </div>
            ) : !hasAccess ? (
                /* --- ACCESS RESTRICTED SCREEN --- */
                <div className="flex-1 flex flex-col items-center justify-center p-8 bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl relative overflow-hidden text-center shadow-2xl">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#02DFC2]/5 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative z-10 max-w-lg mx-auto flex flex-col items-center">
                        <div className="relative mb-6">
                            <div className="w-20 h-20 bg-[#12151C] border border-[#1A1D24] rounded-2xl flex items-center justify-center shadow-inner">
                                <Lock className="w-8 h-8 text-[#02DFC2]" />
                            </div>
                            <div className="absolute -bottom-2 -right-2 p-1.5 bg-black border border-[#1A1D24] rounded-lg">
                                <Shield className="w-4 h-4 text-[#F97316]" />
                            </div>
                        </div>

                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#12151C] border border-[#1A1D24] rounded-full text-[10px] text-[#02DFC2] uppercase tracking-[0.2em] mb-4">
                            <Radio className="w-3 h-3 text-[#02DFC2] animate-pulse" />
                            CLEARANCE_LEVEL_RESTRICTED
                        </div>

                        <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-2">
                            Private Forums Intel Restricted
                        </h3>

                        <p className="text-[12px] text-slate-400 font-mono leading-relaxed mb-6">
                            Access to Underground Private Forums Intelligence, live signal decrypts, and comms cluster monitoring is restricted to elevated license tiers.
                        </p>

                        <div className="w-full bg-black/60 border border-[#1A1D24] rounded-xl p-4 mb-6 text-left space-y-3">
                            <div className="flex items-center justify-between text-[11px] pb-3 border-b border-[#1A1D24]">
                                <span className="text-slate-500 uppercase tracking-wider">Current License Tier:</span>
                                <span className="font-bold text-slate-300 uppercase px-2 py-0.5 bg-[#12151C] rounded border border-[#1A1D24]">
                                    {rawLicenseType || 'Standard'}
                                </span>
                            </div>
                            <div>
                                <span className="text-[10px] text-slate-500 uppercase tracking-widest block mb-2">
                                    Authorized Clearance Tiers:
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    {ALLOWED_LICENSES.map((tier) => (
                                        <span
                                            key={tier}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#02DFC2]/10 border border-[#02DFC2]/30 rounded-md text-[10px] font-bold text-[#02DFC2] uppercase tracking-wider"
                                        >
                                            <Shield className="w-3 h-3" />
                                            {tier}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full">
                            <button
                                onClick={() => navigate('/app/settings')}
                                className="w-full sm:flex-1 py-3 px-6 bg-[#02DFC2] hover:bg-[#02DFC2]/90 text-black text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-[0_0_20px_rgba(2,223,194,0.3)] flex items-center justify-center gap-2"
                            >
                                Upgrade License
                            </button>
                            <button
                                onClick={() => { window.location.href = 'mailto:support@shieldtechub.com'; }}
                                className="w-full sm:flex-1 py-3 px-6 bg-[#12151C] hover:bg-[#1A1D24] border border-[#1A1D24] hover:border-slate-600 text-slate-300 text-xs font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2"
                            >
                                Contact Sales
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* --- STATS GRID --- */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {statsLoading ? (
                            Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-4 animate-pulse h-20" />
                            ))
                        ) : (
                            <>
                                {[
                                    { label: 'Monitored_Sectors', val: stats?.total_channels, color: '#02DFC2', icon: Hash },
                                    { label: 'Capture_Volume', val: stats?.total_messages?.toLocaleString(), color: '#3B82F6', icon: MessageSquare },
                                    { label: 'Burst_60M', val: stats?.messages_last_hour?.toLocaleString(), color: '#50A970', icon: TrendingUp },
                                    { label: 'Intercept_24H', val: stats?.messages_last_24h?.toLocaleString(), color: '#F97316', icon: Calendar }
                                ].map((stat, i) => (
                                    <div key={i} className="bg-[#050505] border border-[#1A1D24] rounded-xl p-4 flex items-center justify-between group hover:bg-[#0A0B0E] transition-colors border-t-2" style={{ borderTopColor: stat.color }}>
                                        <div>
                                            <p className="text-slate-500 text-[9px] font-bold uppercase tracking-[0.15em] mb-1">{stat.label}</p>
                                            <h3 className="text-xl font-bold text-white tracking-tight">{stat.val || 0}</h3>
                                        </div>
                                        <stat.icon className="w-5 h-5 opacity-20 group-hover:opacity-100 transition-opacity" style={{ color: stat.color }} />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {/* --- MAIN WORKSPACE --- */}
                    <div className="flex-1 flex bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl overflow-hidden shadow-2xl relative min-h-0">

                        {/* LEFT PANE: Directory */}
                        <div className={`w-full lg:w-[320px] flex-shrink-0 flex flex-col border-r border-[#1A1D24] bg-[#050505]/50 ${selectedChat ? 'hidden lg:flex' : 'flex'}`}>
                            <div className="p-5 border-b border-[#1A1D24]">
                                <h3 className="text-[11px] font-bold text-slate-400 tracking-[0.2em] mb-4 uppercase flex items-center gap-2">
                                    <Shield className="w-3.5 h-3.5 text-[#02DFC2]" /> Channels Registry
                                </h3>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                                    <input
                                        type="text"
                                        placeholder="FILTER SOURCES..."
                                        value={globalSearch}
                                        onChange={(e) => setGlobalSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 bg-black border border-[#1A1D24] rounded-lg text-[11px] text-[#02DFC2] uppercase tracking-wider focus:outline-none focus:border-[#02DFC2] transition-all"
                                    />
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
                                {channelsLoading ? (
                                    <div className="flex flex-col items-center justify-center h-full opacity-30 gap-3">
                                        <Loader2 className="w-6 h-6 animate-spin text-[#02DFC2]" />
                                        <span className="text-[10px] uppercase tracking-widest text-[#02DFC2]">Scanning Matrix...</span>
                                    </div>
                                ) : (
                                    filteredChats.map(chat => {
                                        const isSelected = selectedChat?.hash_id === chat.hash_id;
                                        return (
                                            <div key={chat.hash_id} onClick={() => handleChatSelect(chat)}
                                                className={`p-3 rounded-xl cursor-pointer transition-all border ${isSelected ? 'bg-[#02DFC2]/5 border-[#02DFC2]/30 shadow-[inset_0_0_15px_rgba(2,223,194,0.05)]' : 'bg-transparent border-transparent hover:bg-[#12151C] hover:border-[#1A1D24]'}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex-shrink-0 border flex items-center justify-center font-bold text-lg ${isSelected ? 'border-[#02DFC2]/50 bg-[#02DFC2]/10 text-[#02DFC2]' : 'border-[#1A1D24] bg-[#0A0B0E] text-slate-500'}`}>
                                                        {chat.photo_url ? <img src={chat.photo_url} className="w-full h-full rounded-lg object-cover" /> : chat.title[0]}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className={`text-[12px] font-bold truncate uppercase ${isSelected ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}>{chat.title}</h4>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <span className="text-[9px] font-bold text-slate-600 tracking-widest">{getEntityLabel(chat.entity_type)}</span>
                                                            <span className={`text-[10px] font-bold ${isSelected ? 'text-[#02DFC2]' : 'text-slate-500'}`}>{chat.message_count} PKTS</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* RIGHT PANE: Workspace */}
                        <div className={`flex-1 flex flex-col bg-[#050505] relative ${!selectedChat ? 'hidden lg:flex' : 'flex'}`}>
                            {!selectedChat ? (
                                <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                                    <div className="w-20 h-20 bg-[#0A0B0E] border border-[#1A1D24] rounded-3xl flex items-center justify-center mb-6 shadow-2xl">
                                        <Radio className="w-8 h-8 text-slate-700 animate-pulse" />
                                    </div>
                                    <h3 className="text-lg font-bold text-white uppercase tracking-[0.3em] mb-3">Initialize Signal Intercept</h3>
                                    <p className="text-[11px] text-slate-500 max-w-xs uppercase tracking-widest leading-relaxed">Select a communication node from the sector registry to begin decryption of raw signal data.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Feed Header */}
                                    <div className="p-5 border-b border-[#1A1D24] bg-[#0A0B0E] flex items-center justify-between z-10">
                                        <div className="flex items-center gap-4 min-w-0 pr-4">
                                            <button onClick={() => setSelectedChat(null)} className="lg:hidden p-2 text-slate-400 hover:text-white"><ArrowLeft /></button>
                                            <div className="w-12 h-12 rounded-xl bg-[#02DFC2]/10 border border-[#02DFC2]/30 flex items-center justify-center text-xl font-bold text-[#02DFC2] flex-shrink-0">
                                                {selectedChat.title[0]}
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="text-lg font-bold text-white uppercase tracking-tight truncate">{selectedChat.title}</h3>
                                                <div className="flex items-center gap-3 text-[10px] font-bold text-[#02DFC2] uppercase tracking-[0.1em]">
                                                    <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {selectedChat.hash_id.slice(0, 12)}</span>
                                                    <span className="text-slate-800">•</span>
                                                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {selectedChat.subscribers} CLUSTER_PEERS</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <div className="relative hidden md:block w-72">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-600" />
                                                <input
                                                    type="text"
                                                    placeholder="GREP KEYWORDS..."
                                                    value={messageSearch}
                                                    onChange={(e) => setMessageSearch(e.target.value)}
                                                    className="w-full pl-9 pr-4 py-2 bg-black border border-[#1A1D24] rounded-lg text-[10px] text-[#02DFC2] uppercase tracking-wider focus:outline-none focus:border-[#02DFC2]"
                                                />
                                            </div>
                                            <button className="p-2.5 bg-[#0A0B0E] border border-[#1A1D24] text-slate-500 hover:text-[#02DFC2] transition-colors rounded-lg"><Download className="w-4 h-4" /></button>
                                        </div>
                                    </div>

                                    {/* Signal Feed */}
                                    <div className="flex-1 overflow-y-auto custom-scrollbar p-6 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:32px_32px]">
                                        <div className="max-w-4xl mx-auto space-y-6">
                                            {chatMessagesLoading ? (
                                                Array.from({ length: 3 }).map((_, i) => (
                                                    <div key={i} className="animate-pulse bg-[#0A0B0E] border border-[#1A1D24] rounded-xl h-40" />
                                                ))
                                            ) : visibleMessages.length === 0 ? (
                                                <div className="text-center py-20 opacity-20 uppercase tracking-[0.5em] text-slate-400">Zero query hits in buffer</div>
                                            ) : (
                                                visibleMessages.map((msg) => (
                                                    <div key={msg.id} className="group bg-[#0A0B0E] border border-[#1A1D24] hover:border-[#02DFC2]/40 rounded-xl overflow-hidden transition-all shadow-xl">
                                                        <div className="bg-black/50 px-5 py-3 border-b border-[#1A1D24] flex items-center justify-between">
                                                            <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                                                <Clock className="w-3.5 h-3.5 text-[#02DFC2]" />
                                                                <span>{new Date(msg.date).toISOString().replace('T', ' ').substring(0, 19)} UTC</span>
                                                            </div>
                                                            <div className="flex items-center gap-4 text-[10px] font-bold text-slate-600">
                                                                <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> {msg.views}</span>
                                                                <span className="px-2 py-0.5 bg-[#12151C] rounded text-[#02DFC2]">ID_{msg.message_id}</span>
                                                            </div>
                                                        </div>
                                                        <div className="p-6 relative">
                                                            <p className="text-[14px] text-slate-300 whitespace-pre-wrap leading-relaxed font-sans">
                                                                <HighlightText text={msg.text} highlight={messageSearch} />
                                                            </p>
                                                            <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => handleCopy(msg.text, msg.id)}
                                                                    className="flex items-center gap-2 px-4 py-2 bg-[#02DFC2] text-black text-[10px] font-bold uppercase tracking-widest rounded-lg shadow-[0_0_15px_rgba(2,223,194,0.4)]"
                                                                >
                                                                    {copiedId === msg.id ? 'SYNK_OK' : <><Copy className="w-3.5 h-3.5" /> CLONE_PAYLOAD</>}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #02DFC230; }
            `}} />
        </div>
    );
};

export default TelegramDashboard;